import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import Dialog, { DialogProps } from "components/Dialog";
import Spacer from "components/Spacer";
import { EventAvailabilityDetails, EventDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import useCurrentUser from "hooks/useCurrentUser";
import { updateAccountMemberRequest } from "queries/account.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React from "react";
import { View } from "react-native";
import toast from "utils/toast";

interface TDCDialogProps extends DialogProps {
  event: EventAvailabilityDetails | EventDetails;
}

const TDCDialog: React.FC<TDCDialogProps> = ({
  visible,
  setVisible,
  onOk,
  event,
  ...rest
}) => {
  const { currentUser } = useCurrentUser(); // we know we are primary member if the dialog is open
  const { account, fetchAccount } = useAccount();
  const { mutateAsync: updateAccountMember, isLoading: updatingAccountMember } =
    useMutation(updateAccountMemberRequest);
  const { currentMember } = useCurrentUser();
  const { data: tenantBranding } = useFetchTenantBrandingQuery();

  const handleUpdateAccountMember = async () => {
    try {
      await updateAccountMember({
        accountId: account!.id,
        memberId: currentMember!.id,
        extUsername: currentMember!.user.email,
        eventId: "eventId" in event ? event.eventId : event.id,
      });
      toast.success(
        "Great! We have successfully linked your account. You may now begin making your first reservation.",
      );
      fetchAccount();
    } finally {
      setVisible!(false);
    }
  };

  const isMLB = tenantBranding?.type === "BASEBALL";

  if (!currentUser) return null;

  return (
    <Dialog visible={visible} setVisible={setVisible} {...rest}>
      <Text category="h6">
        Link your {isMLB ? "MLB Ballpark" : "MiLB ProVenue"} account
      </Text>

      <Spacer />

      <Text>
        To make your first reservation, first we need to link your{" "}
        {isMLB ? "MLB Ballpark" : "MiLB ProVenue"} account
      </Text>

      <Spacer />

      <Text>
        {isMLB
          ? "Your entry barcodes will be delivered to your mobile device, inside the MLB Ballpark app."
          : "Once linked, your tickets will be delivered here directly to your FanRally account."}
      </Text>

      <Spacer />

      <View>
        {isMLB && (
          <View>
            <Text>
              Please ensure you are either logged into the MLB Ballpark app with
              the email address listed below, or add it to your MLB Verified
              Emails in the account settings.
            </Text>
            <Text style={{ marginVertical: 15, fontWeight: "700" }}>
              {currentUser.email}
            </Text>
          </View>
        )}
        <Button
          loading={updatingAccountMember}
          style={{ alignSelf: "flex-start" }}
          onPress={handleUpdateAccountMember}
        >
          {isMLB ? "Link MLB Account" : "Link MiLB ProVenue Account"}
        </Button>
      </View>
    </Dialog>
  );
};

export default TDCDialog;
